import type {
    MonthSelectorNavigationDateConstantType,
    MonthSelectorSelectedDateConstantType,
    MonthSelectorRangeResultConstantType,
    MonthSelectorNavigationConstantType,
    MonthSelectorMonthTitleConstantType,
    CalendarMonthSelectionConstantType,
    MonthSelectorTabStopConstantType,
    MonthSelectInitialGeneratorType,
    MonthSelectorMonthsConstantType,
    MonthSelectorTitleConstantType,
    MonthSelectorPageConstantType,
    MonthSelectorRangeResult,
    MonthSelectorNavigation,
    CalendarMonthSelection,
    MonthOfYearLengthType,
    CalendarMonth
} from "./type";
import moment from "moment";
import {
    formatDateTime
} from "../../helpers/locale/format";
import {
    getHorizontalStep
} from "../../helpers/direction";

export const MONTH_SELECTOR_MONTH_TITLE_FORMATS: Record<MonthOfYearLengthType, Intl.DateTimeFormatOptions> = {
    short: {
        month: "short"
    },
    long: {
        month: "long"
    }
};

export const MONTH_SELECTOR_TITLE_FORMAT: Intl.DateTimeFormatOptions = {
    year: "numeric"
};

export const MONTH_SELECTOR_MONTH_COUNT = 12;

export const MONTH_SELECTOR_COLUMN_COUNT = 3;

export const MONTH_SELECTOR_NAVIGATION_KEYS: Array<string> = [
    "ArrowRight",
    "ArrowLeft",
    "ArrowDown",
    "ArrowUp",
    "PageDown",
    "PageUp",
    "Home",
    "End"
];

export const isValidDate = (date?: Date): date is Date => {
    return date instanceof Date && !Number.isNaN(date.getTime());
};

export const dateSelect = ({
    dateRange,
    variant,
    date
}: MonthSelectInitialGeneratorType): Date => {
    if(variant === "single" && isValidDate(date)) {
        return date;
    }

    if(variant === "range" && dateRange) {
        if(isValidDate(dateRange.start)) return dateRange.start;
        if(isValidDate(dateRange.end)) return dateRange.end;
    }

    return new Date();
};

export const getCalendarMonthSelection = ({
    dateRange,
    variant,
    month,
    date
}: CalendarMonthSelectionConstantType): CalendarMonthSelection => {
    if(variant === "single") {
        return {
            isSelected: !!date && month.isSame(date, "month")
        };
    }

    if(variant !== "range" || !dateRange) {
        return {
            isSelected: false
        };
    }

    const {
        start,
        end
    } = dateRange;

    if(!start || !end || moment(start).isSame(end, "month")) {
        const selectedDate = start ?? end;

        return {
            isSelected: !!selectedDate && month.isSame(selectedDate, "month")
        };
    }

    if(month.isSame(start, "month")) {
        return {
            rangePosition: "start",
            isSelected: false
        };
    }

    if(month.isSame(end, "month")) {
        return {
            rangePosition: "end",
            isSelected: false
        };
    }

    if(month.isBetween(start, end, "month", "()")) {
        return {
            rangePosition: "middle",
            isSelected: false
        };
    }

    return {
        isSelected: false
    };
};

export const getMonthSelectorMonths = ({
    dateRange,
    viewDate,
    maxDate,
    minDate,
    variant,
    date
}: MonthSelectorMonthsConstantType): Array<CalendarMonth> => {
    const yearStart = moment(viewDate).startOf("year");

    const today = moment();

    return Array.from({
        length: MONTH_SELECTOR_MONTH_COUNT
    }, (_, index) => {
        const month = yearStart.clone().add(index, "months");

        const selection = getCalendarMonthSelection({
            dateRange,
            variant,
            month,
            date
        });

        return {
            isDisabled: (!!minDate && month.isBefore(minDate, "month")) || (!!maxDate && month.isAfter(maxDate, "month")),
            rangePosition: selection.rangePosition,
            isToday: month.isSame(today, "month"),
            isSelected: selection.isSelected,
            date: month
        };
    });
};

export const getMonthSelectorRows = (months: Array<CalendarMonth>): Array<Array<CalendarMonth>> => {
    return Array.from({
        length: Math.ceil(months.length / MONTH_SELECTOR_COLUMN_COUNT)
    }, (_, rowIndex) => months.slice(rowIndex * MONTH_SELECTOR_COLUMN_COUNT, (rowIndex + 1) * MONTH_SELECTOR_COLUMN_COUNT));
};

export const getMonthSelectorMonthTitle = ({
    monthOfYearLengthType,
    activeLocale,
    date
}: MonthSelectorMonthTitleConstantType): string => {
    return formatDateTime({
        options: MONTH_SELECTOR_MONTH_TITLE_FORMATS[monthOfYearLengthType],
        locale: activeLocale,
        date
    });
};

export const getMonthSelectorTitle = ({
    activeLocale,
    viewDate
}: MonthSelectorTitleConstantType): string => {
    return formatDateTime({
        options: MONTH_SELECTOR_TITLE_FORMAT,
        locale: activeLocale,
        date: viewDate
    });
};

export const getMonthSelectorNavigation = ({
    viewDate,
    maxDate,
    minDate
}: MonthSelectorNavigationConstantType): MonthSelectorNavigation => {
    return {
        isPreviousDisabled: !!minDate && !moment(viewDate).isAfter(minDate, "year"),
        isNextDisabled: !!maxDate && !moment(viewDate).isBefore(maxDate, "year")
    };
};

export const getMonthSelectorSelectedDate = ({
    month,
    date
}: MonthSelectorSelectedDateConstantType): Date => {
    const selectedDate = month.clone().startOf("month");

    const currentDate = moment(date);

    if(!date || !currentDate.isValid()) {
        return selectedDate.toDate();
    }

    return selectedDate.set({
        date: Math.min(currentDate.date(), selectedDate.daysInMonth()),
        millisecond: currentDate.millisecond(),
        minute: currentDate.minute(),
        second: currentDate.second(),
        hour: currentDate.hour()
    }).toDate();
};

export const getMonthSelectorRangeResult = ({
    multipleSelectMinimumRequiredDayCount,
    multipleSelectDayLimit,
    dateRange,
    month
}: MonthSelectorRangeResultConstantType): MonthSelectorRangeResult => {
    const monthStart = month.clone().startOf("month");

    if(!dateRange || !dateRange.start) {
        return {
            dateRange: {
                start: monthStart.toDate(),
                end: undefined
            }
        };
    }

    if(monthStart.isSame(dateRange.start, "month")) {
        return {
            dateRange: {
                start: undefined,
                end: undefined
            }
        };
    }

    if(dateRange.end) {
        return {
            dateRange: {
                start: monthStart.toDate(),
                end: undefined
            }
        };
    }

    const monthDifference = monthStart.diff(moment(dateRange.start).startOf("month"), "months");

    if(monthDifference < 0) {
        return {
            dateRange: {
                start: monthStart.toDate(),
                end: undefined
            }
        };
    }

    if(multipleSelectDayLimit && monthDifference >= multipleSelectDayLimit) {
        return {
            errorKey: "maximum-selection-number-of-months-limit-exceeds"
        };
    }

    if(multipleSelectMinimumRequiredDayCount && monthDifference + 1 < multipleSelectMinimumRequiredDayCount) {
        return {
            errorKey: "minimum-selection-number-of-months-required-not-provided"
        };
    }

    return {
        dateRange: {
            end: monthStart.clone().endOf("month").toDate(),
            start: dateRange.start
        }
    };
};

export const getMonthSelectorPageIndex = ({
    viewDate
}: MonthSelectorPageConstantType): number => {
    return moment(viewDate).year();
};

export const getMonthSelectorTabStopIndex = ({
    activeDate,
    months
}: MonthSelectorTabStopConstantType): number => {
    const isFocusable = (monthItem: CalendarMonth) => !monthItem.isDisabled;

    const conditions: Array<(monthItem: CalendarMonth) => boolean> = [
        (monthItem) => !!activeDate && monthItem.date.isSame(activeDate, "month"),
        (monthItem) => monthItem.isSelected || monthItem.rangePosition === "start" || monthItem.rangePosition === "end",
        (monthItem) => monthItem.isToday,
        () => true
    ];

    for(const condition of conditions) {
        const index = months.findIndex((monthItem) => isFocusable(monthItem) && condition(monthItem));

        if(index >= 0) {
            return index;
        }
    }

    return -1;
};

export const getMonthSelectorNavigationDate = ({
    direction,
    maxDate,
    minDate,
    month,
    key
}: MonthSelectorNavigationDateConstantType): moment.Moment | undefined => {
    const rowOffset = month.month() % MONTH_SELECTOR_COLUMN_COUNT;

    const horizontalStep = getHorizontalStep({
        direction,
        key
    });

    const targets: Record<string, () => moment.Moment> = {
        End: () => month.clone().add(MONTH_SELECTOR_COLUMN_COUNT - 1 - rowOffset, "months"),
        ArrowUp: () => month.clone().subtract(MONTH_SELECTOR_COLUMN_COUNT, "months"),
        ArrowDown: () => month.clone().add(MONTH_SELECTOR_COLUMN_COUNT, "months"),
        ArrowRight: () => month.clone().add(horizontalStep, "months"),
        ArrowLeft: () => month.clone().add(horizontalStep, "months"),
        Home: () => month.clone().subtract(rowOffset, "months"),
        PageUp: () => month.clone().subtract(1, "years"),
        PageDown: () => month.clone().add(1, "years")
    };

    let target = targets[key]?.();

    if(!target) {
        return undefined;
    }

    if(minDate && target.isBefore(minDate, "month")) {
        target = moment(minDate).startOf("month");
    }

    if(maxDate && target.isAfter(maxDate, "month")) {
        target = moment(maxDate).startOf("month");
    }

    return target.isSame(month, "month") ? undefined : target;
};
